import { Box, Stack, Typography, Button, CircularProgress } from '@mui/material';
import { Session } from '@supabase/supabase-js';

import { environment } from '@env';

import { useGetDocument } from '@/api/endpoints/document/useGetDocument';
import { DocumentIcon } from '@/assets/icons/DocumentIcon';
import { DownloadCloud01Icon } from '@/assets/icons/DownloadCloud01Icon';

import ChatWidget from '@/components/chat-widget/ChatWidget';
import DocumentSummary from '@/components/document/DocumentSummary';
import MuiPDFDocument from '@/components/document/MuiPDFDocument';

// Import the interface from DocumentExtractPanel for type casting
// interface ExtractedDocumentData {
//   success: boolean;
//   summary?: string;
//   document_type?: string;
//   custom_metadata?: {
//     provider?: string;
//     bill_type?: string;
//     amount_due?: number;
//     due_date?: string;
//     bill_date?: string;
//     account_number?: string;
//     billing_period?: {
//       start?: string;
//       end?: string;
//     };
//     property_address?: string;
//     user?: {
//       first_name?: string;
//       last_name?: string;
//       email?: string | null;
//       phone?: string | null;
//     };
//     payment_status?: string;
//     autopay_enabled?: boolean;
//     electric_details?: {
//       total: number;
//       supply: number;
//       delivery: number;
//       ev_credit?: number;
//     };
//     gas_details?: {
//       total: number;
//       supply: number;
//       delivery: number;
//     };
//     total_usage?: {
//       gas_therms?: number;
//       electric_kwh?: number;
//     };
//   };
//   id?: number;
//   ocred?: boolean;
//   checksum?: string;
//   file_url?: string;
//   data_type?: string;
//   user_uuid?: string | null;
//   created_at?: string;
//   updated_at?: string;
//   website_id?: number | null;
// }

interface DocumentViewerProps {
  id: string;
  session: Session | null;
  layoutType?: 'page' | 'modal';
}

/**
 * Check if a file type supports PDF conversion
 */
const supportsConversion = (mimeType: string, fileName: string): boolean => {
  const supportedMimeTypes = [
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/rtf', // .rtf
    'text/plain', // .txt
    'text/html', // .html
    'application/vnd.oasis.opendocument.text', // .odt
    'application/vnd.oasis.opendocument.spreadsheet', // .ods
    'application/vnd.oasis.opendocument.presentation', // .odp
  ];

  const supportedExtensions = [
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.rtf',
    '.txt',
    '.html',
    '.htm',
    '.odt',
    '.ods',
    '.odp',
  ];

  const hasExtension = supportedExtensions.some(ext =>
    fileName.toLowerCase().endsWith(ext.toLowerCase())
  );

  return supportedMimeTypes.includes(mimeType) || hasExtension;
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({ id, session, layoutType = 'page' }) => {
  const documentUrl = `${environment.api}/document/doc/${id}?token=${session?.access_token}`;

  const fetchDocumentQuery = useGetDocument(id, session);

  // // Check if the document is a lease document by checking category name or ID
  // const isLeaseDocument = useMemo(() => {
  //   const categoryId = fetchDocumentQuery.data?.category_id;
  //   const categoryName = fetchDocumentQuery.data?.category?.name;

  //   // Check if it's category ID 2 or has 'lease' in the category name
  //   if (categoryId !== undefined) {
  //     return categoryId?.toString() === '2';
  //   }

  //   return categoryName?.toLowerCase().includes('lease') || false;
  // }, [fetchDocumentQuery.data]);

  // const leaseExtractionQuery = useLeaseExtract(id, !!session?.access_token && isLeaseDocument);
  // const documentExtractionQuery = useDocumentExtract(
  //   id,
  //   !!session?.access_token && !isLeaseDocument
  // );

  const isImage = fetchDocumentQuery.data?.mime_type?.startsWith('image/');
  const isPdf = fetchDocumentQuery.data?.mime_type === 'application/pdf';
  const isConvertible = fetchDocumentQuery.data
    ? supportsConversion(fetchDocumentQuery.data.mime_type, fetchDocumentQuery.data.original_name)
    : false;

  // Check processing status for convertible documents
  const docStatus = (fetchDocumentQuery.data?.status as string) || '';
  const isProcessing = ['NEW', 'PROCESSING'].includes(docStatus);
  const isPending = docStatus === 'PENDING';
  const hasProcessed = ['READY', 'COMPLETED', 'PROCESSED'].includes(docStatus);
  const hasFailed = docStatus === 'FAILED';

  const renderDocumentContent = () => {
    if (isImage) {
      return (
        <img
          src={documentUrl}
          alt={fetchDocumentQuery.data?.original_name || id}
          style={{ width: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      );
    }

    if (isPdf || (isConvertible && hasProcessed)) {
      // For PDFs or successfully converted documents, use PDF viewer
      return (
        <MuiPDFDocument url={documentUrl} fileName={fetchDocumentQuery.data?.original_name || id} />
      );
    }

    if (isConvertible && isProcessing) {
      // Show processing indicator for convertible documents being processed
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            p: 4,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="h6" color="textSecondary">
            Converting Document
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            {fetchDocumentQuery.data?.original_name} is being converted to PDF for preview. This may
            take a few moments.
          </Typography>
        </Box>
      );
    }

    if (isConvertible && isPending) {
      // Show pending state for convertible documents not yet processed
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            p: 4,
          }}
        >
          <DocumentIcon width={64} height={64} />
          <Typography variant="h6" color="textSecondary">
            Document Pending Processing
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            {fetchDocumentQuery.data?.original_name} will be converted to PDF for preview once
            processing begins.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadCloud01Icon />}
            href={documentUrl}
            download={fetchDocumentQuery.data?.original_name}
            target="_blank"
            sx={{ mt: 2 }}
          >
            Download Original File
          </Button>
        </Box>
      );
    }

    if (isConvertible && hasFailed) {
      // Show failed state for convertible documents that failed processing
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            p: 4,
          }}
        >
          <DocumentIcon width={64} height={64} />
          <Typography variant="h6" color="error">
            Document Processing Failed
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            {fetchDocumentQuery.data?.original_name} could not be converted to PDF for preview.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadCloud01Icon />}
            href={documentUrl}
            download={fetchDocumentQuery.data?.original_name}
            target="_blank"
            sx={{ mt: 2 }}
          >
            Download Original File
          </Button>
        </Box>
      );
    }

    // For non-convertible document types, show download option
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
          p: 4,
        }}
      >
        <DocumentIcon width={64} height={64} />
        <Typography variant="h6" color="textSecondary">
          Document Preview Not Available
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          {fetchDocumentQuery.data?.original_name} cannot be previewed in the browser.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadCloud01Icon />}
          href={documentUrl}
          download={fetchDocumentQuery.data?.original_name}
          target="_blank"
          sx={{ mt: 2 }}
        >
          Download File
        </Button>
      </Box>
    );
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      sx={{ flex: '1 1 100%', overflow: 'hidden', position: 'relative' }}
    >
      <Box sx={{ height: '100%', overflow: 'auto', flex: { xs: '1 1 100%', md: '7 0 0%' } }}>
        {renderDocumentContent()}
      </Box>
      <Box sx={{ height: '100%', overflow: 'auto', flex: { xs: '1 1 100%', md: '5 0 0%' } }}>
        <DocumentSummary documentId={id} />
      </Box>
      <ChatWidget documentId={id} />
    </Stack>
  );
};

export default DocumentViewer;
